(() => {
  'use strict';
  const instances = new WeakMap();
  const gs = window.gsap;
  if (gs && window.ScrollTrigger) gs.registerPlugin(window.ScrollTrigger);
  function mount(root) {
    if (instances.has(root)) return;
    const controller = new AbortController();
    const signal = controller.signal;
    const listen = (el, type, fn) => el?.addEventListener(type, fn, { signal });
    const motion = matchMedia('(prefers-reduced-motion: reduce)');
    const home = root.querySelector('[data-dt-home]');
    const worlds = [...root.querySelectorAll('[data-dt-world]')];
    const intro = root.querySelector('[data-dt-intro]');
    const replay = document.querySelector('[data-dt-replay]');
    let context, introTween, zoomTween, timer, focusBefore, oldOverflow, inertStates = [];
    let introActive = false;
    let selected = null;
    root.classList.add('is-enhanced');
    function closeFilms() {
      root.querySelectorAll('dialog[open]').forEach(dialog => { dialog.querySelector('video')?.pause(); dialog.close(); });
    }
    function finishIntro(restoreFocus = true) {
      if (!introActive) return;
      introActive = false;
      clearTimeout(timer);
      introTween?.kill();
      intro.hidden = true;
      document.body.style.overflow = oldOverflow;
      inertStates.forEach(([el, value]) => { el.inert = value; });
      inertStates = [];
      if (restoreFocus && focusBefore?.isConnected) focusBefore.focus({ preventScroll: true });
    }
    function playIntro() {
      if (motion.matches || !gs || root.dataset.designMode === 'true') return;
      finishIntro(false);
      introActive = true;
      focusBefore = document.activeElement;
      oldOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      inertStates = [home, ...worlds, ...document.querySelectorAll('.dt-header,.dt-footer')].map(el => [el, el.inert]);
      inertStates.forEach(([el]) => { el.inert = true; });
      intro.hidden = false;
      const lamp = intro.querySelector('.dt-lamp-stage');
      const light = intro.querySelector('.dt-lamp-light');
      const bulb = intro.querySelector('.dt-bulb');
      const brand = intro.querySelector('.dt-intro-brand');
      gs.set(intro, { opacity: 1 });
      gs.set([light, bulb], { opacity: .05 });
      gs.set(lamp, { opacity: .3, y: -30 });
      gs.set(brand, { opacity: 0, y: 22, scale: .96 });
      intro.querySelector('[data-dt-skip]').focus({ preventScroll: true });
      introTween = gs.timeline({ onComplete: () => finishIntro() })
        .to(lamp, { opacity: 1, y: 0, duration: 1.1 })
        .to([light, bulb], { opacity: .9, duration: .08 }, .65)
        .to([light, bulb], { opacity: .06, duration: .12 }, .77)
        .to([light, bulb], { opacity: .75, duration: .1 }, 1.05)
        .to([light, bulb], { opacity: .08, duration: .12 }, 1.22)
        .to([light, bulb], { opacity: 1, duration: .35 }, 1.55)
        .to(brand, { opacity: 1, y: 0, scale: 1, duration: 1.5, ease: 'power2.out' }, 2)
        .to(intro, { opacity: 0, duration: .9 }, 5.5);
      timer = setTimeout(() => finishIntro(), 8000);
      try { sessionStorage.setItem('dattero-intro-seen', '1'); } catch (_) { /* Storage may be blocked. */ }
    }
    function animate() {
      context?.revert();
      if (!gs || motion.matches) return;
      context = gs.context(() => {
        if (!selected) {
          root.querySelectorAll('.dt-float').forEach((el, i) => gs.to(el, { y: -10, rotation: i === 1 ? 1 : -1, duration: 2.5 + i * .3, repeat: -1, yoyo: true, ease: 'sine.inOut' }));
          if (window.ScrollTrigger) {
            gs.utils.toArray('.dt-reveal', home).forEach(el => gs.from(el, { y: 32, opacity: 0, duration: .9, scrollTrigger: { trigger: el, start: 'top 94%', once: true } }));
            gs.to(home.querySelector('.dt-editorial-photo img'), { yPercent: -8, ease: 'none', scrollTrigger: { trigger: home.querySelector('.dt-editorial-photo'), start: 'top bottom', end: 'bottom top', scrub: .7 } });
            gs.to(home.querySelectorAll('.dt-scroll-packet'), { y: 45, stagger: .08, ease: 'none', scrollTrigger: { trigger: home.querySelector('.dt-lineup'), start: 'top 30%', end: 'bottom top', scrub: .7 } });
          }
        } else gs.from(selected.querySelectorAll('.dt-world-copy > *'), { y: 20, opacity: 0, duration: .65, stagger: .06 });
      }, root);
      window.ScrollTrigger?.refresh();
    }
    function route(initial = false) {
      let hash;
      try { hash = decodeURIComponent(location.hash.slice(1)); } catch (_) { hash = ''; }
      const next = worlds.find(w => w.id === hash);
      if (hash.startsWith('dt-details-')) return;
      closeFilms();
      zoomTween?.kill();
      gs?.set(root.querySelectorAll('[data-dt-card]'), { clearProps: 'opacity,transform' });
      selected = next || null;
      home.hidden = !!selected;
      worlds.forEach(w => { w.hidden = w !== selected; });
      document.body.classList.toggle('dt-product-open', !!selected);
      animate();
      if (selected) {
        window.scrollTo({ top: 0, behavior: 'instant' });
        if (!initial) selected.querySelector('h2')?.focus({ preventScroll: true });
      } else if (hash) {
        const target = document.getElementById(hash);
        requestAnimationFrame(() => target?.scrollIntoView({ behavior: 'instant', block: 'start' }));
      } else if (!initial) window.scrollTo({ top: 0, behavior: 'instant' });
    }
    listen(window, 'hashchange', () => { finishIntro(false); route(); });
    listen(document, 'click', event => {
      const anchor = event.target.closest('a');
      if (!anchor || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || anchor.target === '_blank') return;
      const url = new URL(anchor.href, location.href);
      if (url.origin !== location.origin || url.pathname !== location.pathname) return;
      const next = worlds.find(w => '#' + w.id === url.hash);
      if (!next) return;
      event.preventDefault();
      finishIntro(false);
      const go = () => { if (location.hash === url.hash) route(); else location.hash = url.hash; };
      const card = anchor.closest('[data-dt-card]');
      if (card && gs && !motion.matches) {
        zoomTween?.kill();
        zoomTween = gs.timeline({ onComplete: go }).to(card, { scale: 1.12, duration: .4, ease: 'power2.inOut' }, 0)
          .to([...root.querySelectorAll('[data-dt-card]')].filter(c => c !== card), { opacity: .15, duration: .35 }, 0);
      } else go();
    });
    listen(root, 'click', event => {
      const thumb = event.target.closest('[data-dt-image]');
      if (thumb) {
        const gallery = thumb.closest('.dt-gallery');
        const img = gallery.querySelector('[data-dt-gallery-image]');
        img.src = thumb.dataset.dtImage;
        img.alt = `${thumb.closest('.dt-world').querySelector('.dt-details-copy h3').textContent} — ${thumb.dataset.caption}`;
        gallery.querySelector('[data-dt-full-image]').href = thumb.dataset.dtImage;
        gallery.querySelector('[data-dt-gallery-caption]').textContent = thumb.dataset.caption;
        gallery.querySelectorAll('[data-dt-image]').forEach(b => b.setAttribute('aria-pressed', String(b === thumb)));
      }
      const play = event.target.closest('[data-dt-play]');
      if (play) {
        const dialog = play.parentElement.querySelector('dialog');
        dialog.showModal();
        const video = dialog.querySelector('video');
        video.muted = true;
        video.play()?.catch(() => {});
      }
      if (event.target.closest('[data-dt-film-close]')) closeFilms();
      const amazon = event.target.closest('[data-dt-amazon]');
      if (amazon) {
        const detail = { product: amazon.dataset.dtAmazon, url: amazon.href };
        document.dispatchEvent(new CustomEvent('dattero:amazon-click', { detail }));
        if (Array.isArray(window.dataLayer)) window.dataLayer.push({ event: 'dattero_amazon_click', ...detail });
      }
    });
    root.querySelectorAll('dialog').forEach(dialog => {
      listen(dialog, 'close', () => dialog.querySelector('video')?.pause());
      listen(dialog.querySelector('video'), 'ended', () => {
        dialog.close();
        dialog.closest('.dt-world').querySelector('.dt-product-details')?.scrollIntoView({ behavior: motion.matches ? 'instant' : 'smooth' });
      });
    });
    root.querySelectorAll('.dt-packet-scene').forEach(scene => {
      const tilt = scene.querySelector('.dt-tilt');
      listen(scene, 'pointermove', event => {
        if (!gs || motion.matches || event.pointerType !== 'mouse') return;
        const box = scene.getBoundingClientRect();
        gs.to(tilt, { rotationY: ((event.clientX - box.left) / box.width - .5) * 12, rotationX: ((event.clientY - box.top) / box.height - .5) * -9, duration: .4, overwrite: true });
      });
      listen(scene, 'pointerleave', () => gs?.to(tilt, { rotationX: 0, rotationY: 0, duration: .5, overwrite: true }));
    });
    listen(intro.querySelector('[data-dt-skip]'), 'click', () => finishIntro());
    listen(document, 'keydown', event => { if (event.key === 'Escape') finishIntro(); if (introActive && event.key === 'Tab') { event.preventDefault(); intro.querySelector('[data-dt-skip]').focus(); } });
    listen(replay, 'click', playIntro);
    if (replay) replay.hidden = root.dataset.introEnabled !== 'true' || motion.matches;
    listen(motion, 'change', () => { finishIntro(); if (replay) replay.hidden = motion.matches || root.dataset.introEnabled !== 'true'; animate(); });
    route(true);
    let seen = false;
    try { seen = sessionStorage.getItem('dattero-intro-seen') === '1'; } catch (_) { /* Continue without storage. */ }
    if (!seen && !location.hash && !location.search.includes('contact_posted') && root.dataset.introEnabled === 'true') playIntro();
    instances.set(root, () => { finishIntro(false); closeFilms(); controller.abort(); context?.revert(); zoomTween?.kill(); document.body.classList.remove('dt-product-open'); instances.delete(root); });
  }
  const mountAll = () => document.querySelectorAll('[data-dt-root]').forEach(mount);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mountAll, { once: true }); else mountAll();
  document.addEventListener('shopify:section:load', mountAll);
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('[data-dt-root]').forEach(root => instances.get(root)?.()));
  document.addEventListener('click', event => { if (event.target.closest('.dt-menu a')) document.querySelectorAll('.dt-menu[open]').forEach(menu => { menu.open = false; }); });
  document.addEventListener('keydown', event => { if (event.key === 'Escape') document.querySelectorAll('.dt-menu[open]').forEach(menu => { menu.open = false; menu.querySelector('summary').focus(); }); });
})();
